import React from 'react';

import {
  FileCsv,
  FileDefault,
  FileDoc,
  FileImage,
  FilePdf,
  FilePpt,
  FileTxt,
  FileXls,
  FileZip,
} from '../icons';

interface FileIconProps {
  className?: string;
  size?: number;
}

/**
 * Returns the appropriate file icon component based on file extension or MIME type
 * @param filename - The name of the file including extension
 * @param mimeType - Optional MIME type of the file
 * @param props - Props to pass to the icon component (className, size)
 */
export const getFileIcon = (
  filename: string,
  mimeType?: string,
  props?: FileIconProps
): React.ReactElement => {
  // Extract extension from filename
  const extension = filename.toLowerCase().split('.').pop() || '';

  // PDF files
  if (extension === 'pdf' || mimeType === 'application/pdf') {
    return <FilePdf {...props} />;
  }

  // Word documents
  if (
    ['doc', 'docx'].includes(extension) ||
    [
      'application/msword',
      'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
    ].includes(mimeType || '')
  ) {
    return <FileDoc {...props} />;
  }

  // Excel spreadsheets
  if (
    ['xls', 'xlsx'].includes(extension) ||
    [
      'application/vnd.ms-excel',
      'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
    ].includes(mimeType || '')
  ) {
    return <FileXls {...props} />;
  }

  // CSV files
  if (extension === 'csv' || mimeType === 'text/csv') {
    return <FileCsv {...props} />;
  }

  // PowerPoint presentations
  if (
    ['ppt', 'pptx'].includes(extension) ||
    [
      'application/vnd.ms-powerpoint',
      'application/vnd.openxmlformats-officedocument.presentationml.presentation',
    ].includes(mimeType || '')
  ) {
    return <FilePpt {...props} />;
  }

  // Text files
  if (
    ['txt', 'md', 'rtf'].includes(extension) ||
    mimeType?.startsWith('text/')
  ) {
    return <FileTxt {...props} />;
  }

  // Archive files
  if (
    ['zip', 'tar', 'gz', '7z', 'rar'].includes(extension) ||
    [
      'application/zip',
      'application/x-tar',
      'application/gzip',
      'application/x-7z-compressed',
      'application/x-rar-compressed',
    ].includes(mimeType || '')
  ) {
    return <FileZip {...props} />;
  }

  // Image files
  if (
    ['jpg', 'jpeg', 'png', 'gif', 'webp', 'svg', 'bmp', 'tiff', 'tif'].includes(
      extension
    ) ||
    mimeType?.startsWith('image/')
  ) {
    return <FileImage {...props} />;
  }

  // Default fallback
  return <FileDefault {...props} />;
};

/**
 * Helper to get just the component name without rendering
 */
export const getFileIconType = (
  filename: string,
  mimeType?: string
): string => {
  const extension = filename.toLowerCase().split('.').pop() || '';

  // PDF files
  if (extension === 'pdf' || mimeType === 'application/pdf') {
    return 'pdf';
  }

  // Word documents
  if (
    ['doc', 'docx'].includes(extension) ||
    [
      'application/msword',
      'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
    ].includes(mimeType || '')
  ) {
    return 'doc';
  }

  // Excel spreadsheets
  if (
    ['xls', 'xlsx'].includes(extension) ||
    [
      'application/vnd.ms-excel',
      'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
    ].includes(mimeType || '')
  ) {
    return 'xls';
  }

  // CSV files
  if (extension === 'csv' || mimeType === 'text/csv') {
    return 'csv';
  }

  // PowerPoint presentations
  if (
    ['ppt', 'pptx'].includes(extension) ||
    [
      'application/vnd.ms-powerpoint',
      'application/vnd.openxmlformats-officedocument.presentationml.presentation',
    ].includes(mimeType || '')
  ) {
    return 'ppt';
  }

  // Text files
  if (
    ['txt', 'md', 'rtf'].includes(extension) ||
    mimeType?.startsWith('text/')
  ) {
    return 'txt';
  }

  // Archive files
  if (
    ['zip', 'tar', 'gz', '7z', 'rar'].includes(extension) ||
    [
      'application/zip',
      'application/x-tar',
      'application/gzip',
      'application/x-7z-compressed',
      'application/x-rar-compressed',
    ].includes(mimeType || '')
  ) {
    return 'zip';
  }

  // Image files
  if (
    ['jpg', 'jpeg', 'png', 'gif', 'webp', 'svg', 'bmp', 'tiff', 'tif'].includes(
      extension
    ) ||
    mimeType?.startsWith('image/')
  ) {
    return 'image';
  }

  return 'default';
};
